import { z } from 'zod';

/**
 * @module @repo/shared-types/legal
 * Single Source of Truth für die zentralen Rechtstext-Bausteine (`legal`-Schema im
 * Supabase-Projekt `gmolike-id`): Impressum, Datenschutzerklärung und KI-Kennzeichnung.
 * Admin-Board (Profil-Editor, Vorschau, 3-Wege-Merge), SDK (`<LegalPage>`) und Tests
 * leiten ihre Typen ausschließlich von hier ab (`z.infer`, nie lokal redeklariert) —
 * wie `BrandTokensSchema` den `brand_tokens`-`jsonb` typisiert (ADR-0005).
 *
 * Regel-Engine (`evaluateAppliesWhen`) und Renderer (`renderBlock`, `resolveDocument`)
 * liegen bewusst hier und nicht im SDK: Admin-Vorschau/Vollständigkeitsprüfung und
 * SDK-Rendering brauchen DIESELBE Logik — ein zweiter Renderer wäre genau die Drift,
 * die ADR-0010 abschafft. Alle Funktionen dieses Moduls sind rein und netzwerkfrei.
 *
 * Sicherheit: `body_de` und `applies_when` sind DB-Inhalt. Ausgewertet wird
 * whitelist-basiert mit eigenem Tokenizer/Parser — **kein `eval`, kein `new Function`**.
 *
 * Siehe `docs/decisions/ADR-0010` und `docs/legal-standard-konzept.md`
 * (Abschnitt 4: Regel-Engine + Invarianten, Abschnitt 5: Render-Pipeline).
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Kategorie eines Bausteins = die Seite, auf der er erscheint. */
export const LegalCategorySchema = z.enum(['impressum', 'datenschutz', 'ki']);
export type LegalCategory = z.infer<typeof LegalCategorySchema>;

/**
 * Freigabe-Status eines App-Rechtstextprofils. `published` ist der einzige Status,
 * den das SDK öffentlich ausliefert — und er ist gesperrt, solange ein greifender
 * Pflichtbaustein einen leeren Pflicht-Platzhalter hat (`isProfileComplete`).
 */
export const ReviewStatusSchema = z.enum(['draft', 'reviewed', 'published']);
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>;

/** Status eines App-Overrides: aktiv oder „Standard-Änderung gesehen" (3-Wege-Merge). */
export const LegalOverrideStatusSchema = z.enum(['active', 'conflict-ack']);
export type LegalOverrideStatus = z.infer<typeof LegalOverrideStatusSchema>;

/**
 * Teilnahme an der Verbraucherstreitbeilegung (§§ 36, 37 VSBG). Bewusst ein Enum und
 * nicht drei Booleans: die Varianten schließen sich gegenseitig aus — drei Booleans
 * könnten widersprüchlich gesetzt werden („nicht bereit" UND „verpflichtet") und
 * damit einen falschen Rechtstext erzeugen.
 */
export const VsbgTeilnahmeSchema = z.enum(['nicht', 'verpflichtet', 'freiwillig']);
export type VsbgTeilnahme = z.infer<typeof VsbgTeilnahmeSchema>;

/**
 * Whitelist der Enum-Merkmale: Merkmalsname → erlaubte Literale. Nur diese Namen
 * dürfen links von `==` stehen; nur diese Literale rechts davon.
 */
export const LEGAL_ENUM_TRAITS = {
  vsbg_teilnahme: VsbgTeilnahmeSchema.options,
} as const satisfies Readonly<Record<string, readonly string[]>>;

// ---------------------------------------------------------------------------
// Merkmale (traits) — `legal.app_profile.traits` (jsonb)
// ---------------------------------------------------------------------------

/**
 * Merkmale einer App, gegen die `applies_when` und die `<!-- optional: … -->`-Marker
 * ausgewertet werden. Alle Merkmale sind optional; ein nicht gesetztes Merkmal gilt
 * als `false` (bzw. beim Enum als „kein Wert gewählt").
 *
 * Bewusst in zwei Gruppen getrennt:
 *
 * 1. **Betreiber-Stammdaten** — _wer_ ist der Anbieter? Sie beschreiben das
 *    Unternehmen/den Betreiber hinter der App (Rechtsform, Register, Steuer-IDs,
 *    Berufsrecht, Verbraucherbezug) und sind für alle Apps desselben Betreibers
 *    identisch. Sie entscheiden über die Impressum-Bausteine.
 * 2. **Technische App-Merkmale** — _was tut_ die App? Sie beschreiben die konkrete
 *    Verarbeitung (Registrierung, Auth, Hosting, Cookies, Analytics, KI) und
 *    unterscheiden sich von App zu App. Sie entscheiden über Datenschutz-/KI-Bausteine.
 *
 * Die Trennung ist keine Kosmetik: `hasRegistration` schaltet die Account-/Vertrags-
 * bausteine (Art. 6 Abs. 1 lit. b DSGVO) scharf, während `isCommercial` über die
 * Impressum-Basiskategorie entscheidet — dieselbe App kann das eine ohne das andere sein.
 *
 * Unbekannte Schlüssel werden beim Parsen verworfen (kein `.strict()`), damit ein
 * älterer Client eine neuere `traits`-Zeile weiterhin lesen kann. Dass jeder in den
 * Bausteinen verwendete Merkmalsname hier existiert („ein Merkmal, ein Name"),
 * sichert der CI-Test gegen die Whitelist ab.
 */
export const LegalTraitsSchema = z.object({
  // --- Betreiber-Stammdaten -------------------------------------------------
  /** Geschäftsmäßiger digitaler Dienst i.S.d. § 5 DDG → Impressum-Basisbaustein. */
  isCommercial: z.boolean().optional(),
  /** Telemedium, nicht ausschließlich persönlich/familiär → § 18 Abs. 1 MStV. */
  isTelemediaNonPrivate: z.boolean().optional(),
  /** Juristische Person (schaltet Rechtsform-/Vertretungs-Zeilen). */
  isLegalEntity: z.boolean().optional(),
  /** Eingetragen in Handels-/Vereins-/Partnerschafts-/Genossenschaftsregister. */
  isRegisteredCompany: z.boolean().optional(),
  hasVatId: z.boolean().optional(),
  hasWirtschaftsId: z.boolean().optional(),
  /** Es werden (freiwillig) Angaben zum Kapital der Gesellschaft gemacht. */
  disclosesCapital: z.boolean().optional(),
  /** Nicht alle in Geld zu leistenden Einlagen sind eingezahlt. */
  hasOutstandingContributions: z.boolean().optional(),
  /** Tätigkeit bedarf behördlicher Zulassung → Aufsichtsbehörde. */
  requiresLicense: z.boolean().optional(),
  /** Reglementierter Beruf i.S.d. Berufsanerkennungsrichtlinie. */
  isRegulatedProfession: z.boolean().optional(),
  inLiquidation: z.boolean().optional(),
  /** Journalistisch-redaktionell gestaltete Inhalte → § 18 Abs. 2 MStV. */
  hasEditorialContent: z.boolean().optional(),
  /** Vermittlungsdienst i.S.d. DSA → Kontaktstellen Art. 11, 12 DSA. */
  isIntermediaryService: z.boolean().optional(),
  targetsConsumers: z.boolean().optional(),
  /** Mehr als zehn Beschäftigte am 31.12. des Vorjahres (§ 36 Abs. 3 VSBG). */
  hasMoreThanTenEmployees: z.boolean().optional(),
  /** Datenschutzbeauftragter nach Art. 37 DSGVO benannt. */
  hasDataProtectionOfficer: z.boolean().optional(),
  /** Telefonnummer als zweiter Kontaktweg vorhanden (EuGH C-298/07). */
  hasPhone: z.boolean().optional(),

  // --- Technische App-Merkmale ---------------------------------------------
  /** Nutzer können ein Konto anlegen → Art. 6 Abs. 1 lit. b DSGVO. */
  hasRegistration: z.boolean().optional(),
  usesSupabaseAuth: z.boolean().optional(),
  usesCloudflare: z.boolean().optional(),
  usesCookies: z.boolean().optional(),
  usesAnalytics: z.boolean().optional(),
  /** Ausschließlich automatisierte Einzelentscheidung i.S.d. Art. 22 DSGVO. */
  usesAutomatedDecisions: z.boolean().optional(),
  /** Irgendein KI-System im Einsatz → Basis der KI-Kennzeichnung (Art. 50 KI-VO). */
  aiSystems: z.boolean().optional(),
  aiChatbot: z.boolean().optional(),
  aiGeneratesContent: z.boolean().optional(),
  aiDeepfakes: z.boolean().optional(),

  // --- Enum-Merkmale -------------------------------------------------------
  /**
   * Teilnahme an der Verbraucherstreitbeilegung. Snake_case, weil der Name als
   * Literal in den Bausteinen steht (`vsbg_teilnahme == 'freiwillig'`).
   */
  vsbg_teilnahme: VsbgTeilnahmeSchema.optional(),
});
export type LegalTraits = z.infer<typeof LegalTraitsSchema>;

/** Name eines Enum-Merkmals (aktuell nur `vsbg_teilnahme`). */
export type LegalEnumTrait = keyof typeof LEGAL_ENUM_TRAITS;

/** Name eines booleschen Merkmals. */
export type LegalBooleanTrait = Exclude<keyof LegalTraits, LegalEnumTrait>;

/**
 * Whitelist der booleschen Merkmale — aus `LegalTraitsSchema` abgeleitet, damit es
 * genau eine Definitionsstelle gibt (Schema = SSOT, Liste = Ableitung).
 */
export const LEGAL_BOOLEAN_TRAITS: readonly LegalBooleanTrait[] = Object.keys(
  LegalTraitsSchema.shape,
).filter((name): name is LegalBooleanTrait => !(name in LEGAL_ENUM_TRAITS));

// ---------------------------------------------------------------------------
// Platzhalter, Bausteine, Profil, Override
// ---------------------------------------------------------------------------

/** Erlaubte Form eines Platzhalternamens: `snake_case`, klein, ohne führende Zahl. */
export const LEGAL_PLACEHOLDER_NAME_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

/**
 * Platzhalter-Werte einer App (`legal.app_profile.placeholders`, jsonb):
 * `{ anbieter_name: 'Musterfirma GmbH', kontakt_email: 'info@…' }`.
 * Schlüssel werden gegen `snake_case` validiert, damit sie exakt der `{{ … }}`-Form
 * in `body_de` entsprechen; ein leerer Wert gilt beim Rendern als „fehlt".
 */
export const LegalPlaceholdersSchema = z.record(
  z.string().regex(LEGAL_PLACEHOLDER_NAME_PATTERN, 'Platzhaltername: nur snake_case'),
  z.string(),
);
export type LegalPlaceholders = z.infer<typeof LegalPlaceholdersSchema>;

/**
 * Ein Standard-Baustein (`legal.blocks`). `bodyDe` ist Markdown mit `{{ platzhaltern }}`
 * und `<!-- optional: … -->`-Markern; `appliesWhen` entscheidet, ob der Baustein für
 * eine App überhaupt greift; `version` wird bei jeder inhaltlichen Standard-Änderung
 * erhöht und ist die Basis der Konflikterkennung (`override.baseVersion < version`).
 */
export const LegalBlockSchema = z.object({
  /** Stabiler Schlüssel, z. B. `datenschutz.konto-registrierung`. */
  key: z.string().regex(/^[a-z]+\.[a-z0-9-]+$/, 'key: `<kategorie>.<slug>` in Kleinbuchstaben'),
  category: LegalCategorySchema,
  titleDe: z.string().min(1),
  /** Markdown-Template; NIEMALS als Code auswertbar (siehe Modul-Doku). */
  bodyDe: z.string().min(1),
  /** Regel über `LegalTraits`; `'true'` = greift immer. */
  appliesWhen: z.string().min(1).default('true'),
  /** „Erforderlich, SOBALD `appliesWhen` greift" — Grundlage des Publish-Gates. */
  required: z.boolean().default(false),
  /** Normbezug, z. B. `['§ 5 Abs. 1 Nr. 1 DDG']` — macht den Baustein prüfbar. */
  legalRefs: z.array(z.string()).default([]),
  /** Hinweis für den Verwalter; wird NICHT ausgeliefert. */
  editorNote: z.string().nullable().default(null),
  sort: z.number().int(),
  version: z.number().int().nonnegative().default(1),
});
export type LegalBlock = z.infer<typeof LegalBlockSchema>;

/** Rechtstext-Profil einer App (`legal.app_profile`): Werte + Merkmale + Freigabe. */
export const LegalAppProfileSchema = z.object({
  appId: z.string().uuid(),
  placeholders: LegalPlaceholdersSchema.default({}),
  traits: LegalTraitsSchema.default({}),
  reviewStatus: ReviewStatusSchema.default('draft'),
});
export type LegalAppProfile = z.infer<typeof LegalAppProfileSchema>;

/**
 * Bewusste Abweichung einer App von einem Standard-Baustein
 * (`legal.app_block_override`). `baseVersion` ist die geforkte Standard-Version;
 * liegt sie unter `LegalBlock.version`, hat sich der Standard bewegt → 3-Wege-Merge.
 */
export const LegalBlockOverrideSchema = z.object({
  appId: z.string().uuid(),
  blockKey: LegalBlockSchema.shape.key,
  bodyDe: z.string().min(1),
  baseVersion: z.number().int().nonnegative(),
  status: LegalOverrideStatusSchema.default('active'),
});
export type LegalBlockOverride = z.infer<typeof LegalBlockOverrideSchema>;

// ---------------------------------------------------------------------------
// Regel-Engine — Tokenizer + Recursive-Descent-Parser (kein eval)
// ---------------------------------------------------------------------------

/** Ergebnis einer Regel-Auswertung: Wert plus alle gesammelten Beanstandungen. */
export type LegalRuleResult = { value: boolean; warnings: string[] };

type RuleToken = {
  kind: 'operator' | 'paren' | 'identifier' | 'literal';
  value: string;
  pos: number;
};

/** Merkmalszugriff auf das Laufzeit-`jsonb` ohne `any` (Schlüssel sind Strings). */
function traitValue(traits: LegalTraits, name: string): unknown {
  return (traits as Readonly<Record<string, unknown>>)[name];
}

/** Schnelle Whitelist-Prüfung für beliebige (also auch fremde) Bezeichner. */
const BOOLEAN_TRAIT_LOOKUP: ReadonlySet<string> = new Set<string>(LEGAL_BOOLEAN_TRAITS);

/**
 * Zerlegt einen Ausdruck in Tokens. Erlaubt sind ausschließlich `&&`, `||`, `==`,
 * `!`, `(`, `)`, Bezeichner (`[A-Za-z_][A-Za-z0-9_]*`) und `'literale'`. Jedes andere
 * Zeichen bricht die Analyse mit einer Warnung ab — so kann DB-Inhalt weder Property-
 * Ketten (`traits.constructor`) noch Aufrufe (`process.exit(1)`) noch Statement-
 * Trenner (`1;DROP TABLE`) einschmuggeln.
 */
function tokenizeRule(expression: string): { tokens: RuleToken[]; warnings: string[] } {
  const tokens: RuleToken[] = [];
  const warnings: string[] = [];
  let i = 0;
  while (i < expression.length) {
    const char = expression.charAt(i);
    if (/\s/.test(char)) {
      i += 1;
      continue;
    }
    const pair = expression.slice(i, i + 2);
    if (pair === '&&' || pair === '||' || pair === '==') {
      tokens.push({ kind: 'operator', value: pair, pos: i });
      i += 2;
      continue;
    }
    if (char === '!') {
      tokens.push({ kind: 'operator', value: '!', pos: i });
      i += 1;
      continue;
    }
    if (char === '(' || char === ')') {
      tokens.push({ kind: 'paren', value: char, pos: i });
      i += 1;
      continue;
    }
    if (char === "'") {
      const end = expression.indexOf("'", i + 1);
      if (end === -1) {
        warnings.push(`nicht geschlossenes Literal ab Position ${i}`);
        return { tokens, warnings };
      }
      tokens.push({ kind: 'literal', value: expression.slice(i + 1, end), pos: i });
      i = end + 1;
      continue;
    }
    const identifier = /^[A-Za-z_][A-Za-z0-9_]*/.exec(expression.slice(i));
    if (identifier) {
      tokens.push({ kind: 'identifier', value: identifier[0], pos: i });
      i += identifier[0].length;
      continue;
    }
    warnings.push(
      `unerwartetes Zeichen '${char}' an Position ${i} — erlaubt sind Merkmalsnamen, ` +
        `'true', !, &&, ||, ==, Klammern und 'literale'`,
    );
    return { tokens, warnings };
  }
  return { tokens, warnings };
}

/** Löst einen Bezeichner als booleschen Term auf (`true` oder boolesches Merkmal). */
function resolveBooleanTerm(name: string, traits: LegalTraits, warnings: string[]): boolean {
  if (name === 'true') return true;
  if (name in LEGAL_ENUM_TRAITS) {
    warnings.push(
      `'${name}' ist ein Enum-Merkmal und muss verglichen werden ` +
        `(z. B. ${name} == '${LEGAL_ENUM_TRAITS[name as LegalEnumTrait][0]}')`,
    );
    return false;
  }
  if (!BOOLEAN_TRAIT_LOOKUP.has(name)) {
    warnings.push(`unbekanntes Merkmal '${name}' — nicht in der Merkmals-Whitelist`);
    return false;
  }
  return traitValue(traits, name) === true;
}

/** Löst einen Enum-Vergleich `enumName == 'literal'` auf. */
function resolveComparison(
  name: string,
  literal: string,
  traits: LegalTraits,
  warnings: string[],
): boolean {
  if (!(name in LEGAL_ENUM_TRAITS)) {
    const known = BOOLEAN_TRAIT_LOOKUP.has(name) || name === 'true';
    warnings.push(
      known
        ? `'${name}' ist boolesch und kann nicht mit '${literal}' verglichen werden`
        : `unbekanntes Enum-Merkmal '${name}' — nicht in der Merkmals-Whitelist`,
    );
    return false;
  }
  const allowed: readonly string[] = LEGAL_ENUM_TRAITS[name as LegalEnumTrait];
  if (!allowed.includes(literal)) {
    warnings.push(
      `unbekannter Wert '${literal}' für Enum-Merkmal '${name}' ` +
        `(erlaubt: ${allowed.join(', ')})`,
    );
    return false;
  }
  return traitValue(traits, name) === literal;
}

/**
 * Wertet die Tokenliste per Recursive Descent aus.
 * Präzedenz: `!` bindet stärker als `&&`, `&&` stärker als `||`.
 * Es wird bewusst NICHT kurzgeschlossen, damit auch Warnungen aus dem rechten
 * Operanden sichtbar werden (ein defekter Teilausdruck bleibt sonst unentdeckt).
 */
function evaluateRuleTokens(
  tokens: readonly RuleToken[],
  traits: LegalTraits,
  warnings: string[],
): boolean {
  let index = 0;
  const peek = (): RuleToken | undefined => tokens[index];

  const parseOr = (): boolean => {
    let value = parseAnd();
    for (;;) {
      const token = peek();
      if (token?.kind !== 'operator' || token.value !== '||') return value;
      index += 1;
      const right = parseAnd();
      value = value || right;
    }
  };

  const parseAnd = (): boolean => {
    let value = parseUnary();
    for (;;) {
      const token = peek();
      if (token?.kind !== 'operator' || token.value !== '&&') return value;
      index += 1;
      const right = parseUnary();
      value = value && right;
    }
  };

  const parseUnary = (): boolean => {
    const token = peek();
    if (token?.kind === 'operator' && token.value === '!') {
      index += 1;
      return !parseUnary();
    }
    return parseTerm();
  };

  const parseTerm = (): boolean => {
    const token = peek();
    if (!token) {
      warnings.push("Ausdruck endet unerwartet — erwartet wurde ein Merkmal, 'true' oder '('");
      return false;
    }
    if (token.kind === 'paren' && token.value === '(') {
      index += 1;
      const inner = parseOr();
      const closing = peek();
      if (closing?.kind === 'paren' && closing.value === ')') index += 1;
      else warnings.push(`fehlende schließende Klammer zur Klammer an Position ${token.pos}`);
      return inner;
    }
    if (token.kind === 'identifier') {
      index += 1;
      const operator = peek();
      if (operator?.kind === 'operator' && operator.value === '==') {
        index += 1;
        const literal = peek();
        if (literal?.kind !== 'literal') {
          warnings.push(
            `nach '==' an Position ${operator.pos} wurde ein 'literal' in Hochkommata erwartet`,
          );
          return false;
        }
        index += 1;
        return resolveComparison(token.value, literal.value, traits, warnings);
      }
      return resolveBooleanTerm(token.value, traits, warnings);
    }
    warnings.push(
      `unerwartetes Token '${token.value}' an Position ${token.pos} — ` +
        `erwartet wurde ein Merkmal, 'true', '!' oder '('`,
    );
    index += 1;
    return false;
  };

  const result = parseOr();
  const surplus = peek();
  if (surplus) {
    warnings.push(
      `überzähliges Token '${surplus.value}' an Position ${surplus.pos} — ` +
        `der Ausdruck war davor bereits vollständig`,
    );
  }
  return result;
}

/**
 * Wertet einen `applies_when`-Ausdruck (bzw. den Ausdruck eines
 * `<!-- optional: … -->`-Markers) gegen die Merkmale einer App aus.
 *
 * Grammatik (identisch für beide Einsatzorte):
 * ```
 * ausdruck   := term (('&&' | '||') term)*
 * term       := '!'? ( 'true' | trait | vergleich | '(' ausdruck ')' )
 * vergleich  := enumName '==' "'" literal "'"
 * ```
 *
 * Ausgewertet wird whitelist-basiert mit eigenem Tokenizer/Parser — **kein `eval`,
 * kein `new Function`**. Fail-closed: sobald eine Warnung entsteht (unbekanntes
 * Merkmal, unbekanntes Enum-Literal, Syntaxfehler, verbotenes Zeichen), ist das
 * Ergebnis `false`. Ein defekter Ausdruck lässt einen Baustein also „nicht greifen"
 * und meldet das — nie ein stilles `true`, denn ein fälschlich gerenderter Rechtstext
 * ist ein rechtliches Risiko (ADR-0010, Konzept Abschnitt 4).
 *
 * @param expression Der Ausdruck aus der DB, z. B. `isTelemediaNonPrivate && !isCommercial`.
 * @param traits Die Merkmale der App; nicht gesetzte Merkmale gelten als `false`.
 * @returns `value` = greift der Baustein/Abschnitt, `warnings` = alle Beanstandungen.
 */
export function evaluateAppliesWhen(expression: string, traits: LegalTraits): LegalRuleResult {
  const warnings: string[] = [];
  // Defensiv: `applies_when` ist DB-Inhalt und kann trotz NOT NULL leer/nullish ankommen.
  const source = typeof expression === 'string' ? expression.trim() : '';
  if (source === '') {
    return { value: false, warnings: ['leerer Ausdruck — der Baustein greift nicht'] };
  }
  const lexed = tokenizeRule(source);
  warnings.push(...lexed.warnings);
  let value = false;
  if (lexed.tokens.length > 0) {
    value = evaluateRuleTokens(lexed.tokens, traits, warnings);
  } else if (lexed.warnings.length === 0) {
    warnings.push(`Ausdruck '${source}' enthält kein auswertbares Merkmal`);
  }
  return { value: warnings.length === 0 && value, warnings };
}

// ---------------------------------------------------------------------------
// Renderer — Platzhalter + optional-Marker
// ---------------------------------------------------------------------------

/** Ein `<!-- optional: <ausdruck> -->`-Marker; muss allein auf seiner Zeile stehen. */
const OPTIONAL_MARKER_PATTERN = /^\s*<!--\s*optional:\s*(.*?)\s*-->\s*$/;
/** Ein `{{ platzhalter }}`-Token; absichtlich tolerant, damit auch Unsinn erkannt wird. */
const PLACEHOLDER_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;
/** Markdown-Zeilenumbruch (zwei oder mehr Leerzeichen am Zeilenende). */
const HARD_BREAK_PATTERN = /\S[ \t]{2,}$/;
/** Bis zu dieser Länge gilt ein mit `:` endender Rest als reines Label, nicht als Satz. */
const LABEL_MAX_LENGTH = 60;

/** Ergebnis des Renderns eines einzelnen Bausteins. */
export type RenderedBlock = { text: string; missing: string[]; warnings: string[] };

/**
 * Sichtbarer Hinweis für einen nicht auflösbaren Platzhalter. Er ersetzt das rohe
 * `{{ … }}`: die Seite degradiert sichtbar statt zu brechen (Konzept Abschnitt 5) und
 * liefert nie eine unaufgelöste Vorlage aus.
 *
 * @param name Der Platzhaltername.
 * @returns Der Hinweistext, z. B. `[Angabe fehlt: kontakt_email]`.
 */
export function missingPlaceholderHint(name: string): string {
  return `[Angabe fehlt: ${name}]`;
}

/**
 * Liest alle Ausdrücke der `<!-- optional: … -->`-Marker eines Bausteins aus — für
 * die Whitelist-Prüfung in CI und Admin-Validierung („ein Merkmal, ein Name").
 *
 * @param bodyDe Der Markdown-Body eines Bausteins.
 * @returns Die Marker-Ausdrücke in Reihenfolge ihres Auftretens (mit Duplikaten).
 */
export function extractRuleExpressions(bodyDe: string): string[] {
  const expressions: string[] = [];
  for (const line of bodyDe.split('\n')) {
    const match = OPTIONAL_MARKER_PATTERN.exec(line);
    if (match) expressions.push(match[1] ?? '');
  }
  return expressions;
}

/**
 * Ende des Geltungsbereichs eines Markers (exklusiv). Der Bereich beginnt hinter dem
 * Marker und endet an der nächsten Leerzeile, am nächsten Marker, am Textende — oder
 * hinter einer Zeile mit Markdown-Zeilenumbruch (`␣␣`). Letzteres ist entscheidend:
 * die Bausteine nutzen den harten Umbruch, um einzeln schaltbare Zeilen innerhalb
 * EINES Absatzes zu trennen (`Rechtsform: …␣␣` / `{{ anbieter_anschrift }}`). Ohne
 * diese Grenze würde `<!-- optional: isLegalEntity -->` bei natürlichen Personen auch
 * die ladungsfähige Anschrift löschen — die aber § 5 DDG zwingend fordert.
 */
function findMarkerScopeEnd(lines: readonly string[], start: number): number {
  let i = start;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') break;
    if (OPTIONAL_MARKER_PATTERN.test(line)) break;
    i += 1;
    if (HARD_BREAK_PATTERN.test(line)) break;
  }
  return i;
}

/**
 * Trägt der Rest einer Zeile (ohne die nicht auflösbaren Platzhalter) noch Inhalt?
 * Reine Labels („Telefon:", „- **E-Mail:**", „**Umfang des Einsatzes.**") sind ohne
 * ihren Wert wertlos und werden ganz entfernt — es soll kein leeres Label ausgeliefert
 * werden. Ein ganzer Satz mit einem fehlenden Wert am Ende bleibt dagegen stehen und
 * bekommt einen sichtbaren Hinweis, damit kein Rechtstext-Absatz verschwindet.
 */
function isLabelOnly(rest: string): boolean {
  const trimmed = rest.replace(/\s+/g, ' ').trim();
  if (trimmed === '') return true;
  if (new RegExp(`^\\*\\*[^*]{1,${LABEL_MAX_LENGTH}}\\*\\*$`).test(trimmed)) return true;
  const core = trimmed
    .replace(/^[-*+>\s]+/, '')
    .replace(/\*/g, '')
    .trim();
  if (core === '') return true;
  if (!core.endsWith(':')) return false;
  const label = core.slice(0, -1);
  return label.length <= LABEL_MAX_LENGTH && !/[.!?]/.test(label);
}

/**
 * Ersetzt die Platzhalter einer Zeile. Gibt `null` zurück, wenn die Zeile ohne ihren
 * fehlenden Wert nur noch ein Label wäre (dann entfällt sie ganz).
 */
function substitutePlaceholders(
  line: string,
  placeholders: LegalPlaceholders,
  missing: string[],
  warnings: string[],
): string | null {
  if (!line.includes('{{')) return line;
  let withHints = '';
  let withoutGaps = '';
  let gaps = 0;
  let cursor = 0;
  for (const match of line.matchAll(PLACEHOLDER_PATTERN)) {
    const literal = line.slice(cursor, match.index);
    withHints += literal;
    withoutGaps += literal;
    cursor = match.index + match[0].length;
    const name = (match[1] ?? '').trim();
    if (!LEGAL_PLACEHOLDER_NAME_PATTERN.test(name)) {
      warnings.push(`ungültiger Platzhalter '${match[0]}' — erwartet wird '{{ snake_case }}'`);
      withHints += missingPlaceholderHint(name === '' ? '?' : name);
      gaps += 1;
      continue;
    }
    const value = placeholders[name];
    if (typeof value === 'string' && value.trim() !== '') {
      withHints += value;
      withoutGaps += value;
      continue;
    }
    if (!missing.includes(name)) missing.push(name);
    withHints += missingPlaceholderHint(name);
    gaps += 1;
  }
  const tail = line.slice(cursor);
  withHints += tail;
  withoutGaps += tail;
  if (gaps === 0) return withHints;
  return isLabelOnly(withoutGaps) ? null : withHints;
}

/**
 * Räumt den gerenderten Text auf: Leerzeilen nie doppelt, kein Markdown-Zeilenumbruch
 * am Absatzende (er zeigte sonst auf eine entfernte Zeile), keine Leerzeilen am Anfang
 * oder Ende.
 */
function normalizeRenderedText(lines: readonly string[]): string {
  const collapsed: string[] = [];
  for (const raw of lines) {
    const line = raw.trim() === '' ? '' : raw;
    if (line === '' && collapsed[collapsed.length - 1] === '') continue;
    collapsed.push(line);
  }
  for (let i = 0; i < collapsed.length; i += 1) {
    const line = collapsed[i] ?? '';
    const isParagraphEnd = i + 1 === collapsed.length || collapsed[i + 1] === '';
    if (line !== '' && isParagraphEnd) collapsed[i] = line.replace(/[ \t]+$/, '');
  }
  while (collapsed.length > 0 && collapsed[0] === '') collapsed.shift();
  while (collapsed.length > 0 && collapsed[collapsed.length - 1] === '') collapsed.pop();
  return collapsed.join('\n');
}

/**
 * Rendert den Markdown-Body eines Bausteins (Schritte 4 + 5 der Render-Pipeline):
 *
 * 1. `<!-- optional: <ausdruck> -->` wird gegen `traits` ausgewertet. Der Marker selbst
 *    verschwindet immer; sein Geltungsbereich (siehe `findMarkerScopeEnd`) bleibt nur
 *    bei wahrem Ausdruck erhalten.
 * 2. `{{ snake_case }}` wird aus `placeholders` ersetzt. Ein fehlender oder leerer Wert
 *    landet in `missing`; ein rohes `{{ … }}` wird NIE ausgeliefert. Zeilen, die ohne
 *    ihren Wert nur noch ein Label wären („Telefon:"), entfallen vollständig.
 *
 * @param bodyDe Markdown-Template des Bausteins (Standard oder App-Override).
 * @param placeholders Platzhalter-Werte der App.
 * @param traits Merkmale der App (für die optional-Marker).
 * @returns Gerenderter Text, fehlende Platzhalter und alle Beanstandungen.
 */
export function renderBlock(
  bodyDe: string,
  placeholders: LegalPlaceholders,
  traits: LegalTraits,
): RenderedBlock {
  const warnings: string[] = [];
  const missing: string[] = [];
  // Defensiv: `body_de` ist DB-Inhalt; CRLF normalisieren, damit Zeilenlogik trägt.
  const source = typeof bodyDe === 'string' ? bodyDe.replace(/\r\n?/g, '\n') : '';
  const lines = source.split('\n');
  const kept: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    const marker = OPTIONAL_MARKER_PATTERN.exec(line);
    if (!marker) {
      kept.push(line);
      i += 1;
      continue;
    }
    const expression = marker[1] ?? '';
    const rule = evaluateAppliesWhen(expression, traits);
    for (const warning of rule.warnings) {
      warnings.push(`optional-Marker '${expression}': ${warning}`);
    }
    i += 1;
    const scopeEnd = findMarkerScopeEnd(lines, i);
    if (rule.value) kept.push(...lines.slice(i, scopeEnd));
    i = scopeEnd;
  }

  const substituted: string[] = [];
  for (const line of kept) {
    const rendered = substitutePlaceholders(line, placeholders, missing, warnings);
    if (rendered !== null) substituted.push(rendered);
  }

  return { text: normalizeRenderedText(substituted), missing, warnings };
}

// ---------------------------------------------------------------------------
// Dokument-Auflösung, Vollständigkeit, Invarianten
// ---------------------------------------------------------------------------

/** Minimal nötige Felder eines Overrides für die Auflösung („ours"). */
export type LegalOverrideInput = Pick<LegalBlockOverride, 'blockKey' | 'bodyDe'>;

/** Eingabe von {@link resolveDocument}. */
export type ResolveDocumentInput = {
  readonly blocks: readonly LegalBlock[];
  readonly traits: LegalTraits;
  readonly placeholders: LegalPlaceholders;
  /** App-eigene Fassungen; überschreiben den Standard je Baustein. */
  readonly overrides?: readonly LegalOverrideInput[];
  /** Optionaler Kategoriefilter (`impressum` | `datenschutz` | `ki`). */
  readonly category?: LegalCategory;
};

/** Ein gerenderter Baustein im aufgelösten Dokument. */
export type ResolvedLegalBlock = {
  readonly key: string;
  readonly category: LegalCategory;
  readonly titleDe: string;
  readonly sort: number;
  readonly required: boolean;
  /** `override`, wenn die App eine eigene Fassung pflegt, sonst `standard`. */
  readonly source: 'standard' | 'override';
  readonly text: string;
  readonly missing: readonly string[];
  readonly warnings: readonly string[];
};

/** Ergebnis von {@link resolveDocument}. */
export type ResolvedLegalDocument = {
  readonly blocks: readonly ResolvedLegalBlock[];
  /** Alle Bausteine zu einem Markdown-Dokument verbunden. */
  readonly text: string;
  readonly missing: readonly string[];
  readonly warnings: readonly string[];
};

/**
 * Löst das komplette Rechtstext-Dokument einer App auf (Schritte 2–5 der
 * Render-Pipeline): filtert die Bausteine per {@link evaluateAppliesWhen}, sortiert
 * nach `sort`, wählt je Baustein den App-Override ODER den Standard und rendert.
 * Dieselbe Funktion trägt die Admin-Vorschau und das SDK-Rendering.
 *
 * @param input Bausteine, Merkmale, Platzhalter, optionale Overrides + Kategorie.
 * @returns Gerenderte Bausteine, das verbundene Dokument sowie gesammelte
 *   `missing`-Platzhalter und `warnings` (jeweils mit Baustein-Schlüssel als Kontext).
 */
export function resolveDocument(input: ResolveDocumentInput): ResolvedLegalDocument {
  const overrideByKey = new Map(
    (input.overrides ?? []).map((override) => [override.blockKey, override] as const),
  );
  const warnings: string[] = [];
  const missing: string[] = [];
  const resolved: ResolvedLegalBlock[] = [];

  const candidates = input.blocks
    .filter((block) => input.category === undefined || block.category === input.category)
    .sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key));

  for (const block of candidates) {
    const rule = evaluateAppliesWhen(block.appliesWhen, input.traits);
    for (const warning of rule.warnings) {
      warnings.push(`[${block.key}] applies_when: ${warning}`);
    }
    if (!rule.value) continue;
    const override = overrideByKey.get(block.key);
    const rendered = renderBlock(
      override?.bodyDe ?? block.bodyDe,
      input.placeholders,
      input.traits,
    );
    for (const warning of rendered.warnings) warnings.push(`[${block.key}] ${warning}`);
    for (const name of rendered.missing) if (!missing.includes(name)) missing.push(name);
    resolved.push({
      key: block.key,
      category: block.category,
      titleDe: block.titleDe,
      sort: block.sort,
      required: block.required,
      source: override ? 'override' : 'standard',
      text: rendered.text,
      missing: rendered.missing,
      warnings: rendered.warnings,
    });
  }

  return {
    blocks: resolved,
    text: resolved
      .map((block) => block.text)
      .filter((text) => text !== '')
      .join('\n\n'),
    missing,
    warnings,
  };
}

/**
 * Vollständigkeits-Gate für `review_status: 'published'`: `false`, solange ein per
 * `applies_when` greifender `required`-Baustein einen fehlenden Pflicht-Platzhalter
 * hat. Impressum und Datenschutzerklärung dürfen nie unvollständig live gehen
 * (ADR-0010). Optionale Bausteine und nicht greifende Bausteine blockieren nicht.
 *
 * @param blocks Die Standard-Bausteine (bereits auf die Kategorie gefiltert oder alle).
 * @param traits Merkmale der App.
 * @param placeholders Platzhalter-Werte der App.
 * @param overrides Optional die App-eigenen Fassungen — sie werden statt des Standards
 *   geprüft, denn ausgeliefert wird der Override.
 * @returns `true`, wenn jeder greifende Pflichtbaustein vollständig gefüllt ist.
 */
export function isProfileComplete(
  blocks: readonly LegalBlock[],
  traits: LegalTraits,
  placeholders: LegalPlaceholders,
  overrides: readonly LegalOverrideInput[] = [],
): boolean {
  const document = resolveDocument({ blocks, traits, placeholders, overrides });
  return document.blocks.every((block) => !block.required || block.missing.length === 0);
}

/**
 * Die beiden Impressum-Basiskategorien: `isCommercial` ⇒ § 5 DDG,
 * `isTelemediaNonPrivate && !isCommercial` ⇒ § 18 Abs. 1 MStV.
 */
export const IMPRESSUM_BASE_BLOCK_KEYS = [
  'impressum.diensteanbieter',
  'impressum.mstv-basis',
] as const;

/** Ergebnis der Impressum-Basiskategorien-Prüfung. */
export type ImpressumBaseCategoryResult = {
  valid: boolean;
  /** Die tatsächlich greifenden Basisbausteine (0, 1 oder — als Defekt — 2). */
  activeKeys: string[];
  errors: string[];
  warnings: string[];
};

/**
 * Prüft die zentrale Katalog-Invariante: es darf **höchstens eine**
 * Impressum-Basiskategorie greifen, und es muss **mindestens eine** greifen, solange
 * das Angebot nicht ausschließlich privat ist. Beide zugleich = doppeltes Impressum;
 * keine (bei nicht rein privatem Angebot) = kein Impressum = nicht konform. Genau
 * diese zwei Defekte hat das Konsistenz-Audit des Katalogs gefunden (Konzept
 * Abschnitt 4, Invariante 1) — deshalb ist die Prüfung maschinell.
 *
 * @param blocks Die Bausteine des Katalogs (Basisbausteine werden darin gesucht).
 * @param traits Merkmale der App.
 * @returns `valid` plus die greifenden Schlüssel, Fehler und Regel-Warnungen.
 */
export function validateImpressumBaseCategory(
  blocks: readonly LegalBlock[],
  traits: LegalTraits,
): ImpressumBaseCategoryResult {
  const warnings: string[] = [];
  const activeKeys: string[] = [];
  for (const key of IMPRESSUM_BASE_BLOCK_KEYS) {
    const block = blocks.find((candidate) => candidate.key === key);
    if (!block) continue;
    const rule = evaluateAppliesWhen(block.appliesWhen, traits);
    for (const warning of rule.warnings) warnings.push(`[${key}] applies_when: ${warning}`);
    if (rule.value) activeKeys.push(key);
  }

  const errors: string[] = [];
  const purelyPrivate = traits.isCommercial !== true && traits.isTelemediaNonPrivate !== true;
  if (activeKeys.length > 1) {
    errors.push(
      `doppeltes Impressum: ${activeKeys.join(' und ')} greifen gleichzeitig — ` +
        `es darf höchstens eine Impressum-Basiskategorie greifen`,
    );
  } else if (activeKeys.length === 0 && !purelyPrivate) {
    errors.push(
      `kein Impressum: weder ${IMPRESSUM_BASE_BLOCK_KEYS.join(' noch ')} greift, obwohl ` +
        `das Angebot nicht ausschließlich privat ist (§ 5 DDG / § 18 Abs. 1 MStV)`,
    );
  }
  return { valid: errors.length === 0, activeKeys, errors, warnings };
}
